import React, { useEffect } from 'react';

/**
 * CloseFormWrapper Component
 *
 * Reusable wrapper for Close.io web forms with dark theme styling
 * Dynamically loads the Close.io script and injects custom styles into Shadow DOM
 *
 * @param {Object} props
 * @param {string} props.formId - Close.io form ID (e.g., 'form_032Kygkv9nWe1smuJ1QJCM')
 * @param {string} props.title - Form title/heading
 * @param {string} props.backgroundColor - Background color for the form ('dark' or 'white')
 */
const CloseFormWrapper = ({ formId, title, backgroundColor = 'dark' }) => {
  // Load Close.io script
  useEffect(() => {
    // Check if script already exists
    if (document.querySelector('script[src*="closeiocdn.com"]')) {
      return;
    }

    // Create and append script
    const script = document.createElement('script');
    script.src = 'https://webforms.closeiocdn.com/webforms.js';
    script.type = 'module';
    script.crossOrigin = 'anonymous';
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: Remove script if component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Inject custom styles into Shadow DOM
  useEffect(() => {
    const injectStylesToShadowDOM = () => {
      const closeForm = document.querySelector('close-form');

      if (!closeForm) {
        return false;
      }

      // Check if shadow root exists
      const shadowRoot = closeForm.shadowRoot;
      if (!shadowRoot) {
        return false;
      }

      // Remove existing styles if present to allow updates
      const existingStyle = shadowRoot.querySelector('#custom-styles');
      if (existingStyle) {
        existingStyle.remove();
      }

      // Create style element with theme-based styling
      const isWhiteTheme = backgroundColor === 'white';
      const style = document.createElement('style');
      style.id = 'custom-styles';
      style.textContent = `
        /* Form container styling */
        * {
          box-sizing: border-box;
        }

        /* Headings */
        h1, h2, h3 {
          color: ${isWhiteTheme ? '#1a1a1a' : '#ffffff'};
          font-weight: 600;
          margin-bottom: 30px;
          font-size: 32px;
        }

        /* Labels */
        label {
          color: ${isWhiteTheme ? '#333333' : '#e5e5e5'};
          font-weight: 500;
          font-size: 14px;
          margin-bottom: 8px;
          display: block;
        }

        label span {
          color: ${isWhiteTheme ? '#333333' : '#e5e5e5'};
          font-size: 14px;
        }

        /* Input fields */
        input,
        select,
        textarea {
          background-color: ${isWhiteTheme ? '#ffffff' : '#2d2d2d'};
          border: 1px solid ${isWhiteTheme ? '#d1d5db' : '#404040'};
          border-radius: 8px;
          color: ${isWhiteTheme ? '#1a1a1a' : '#ffffff'} !important;
          padding: 12px 16px;
          font-size: 14px;
          margin-bottom: 2px !important;
          width: 100%;
          transition: border-color 0.2s ease;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }


        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #391b6d;
        }

        textarea {
          min-height: 100px;
          resize: vertical;
        }

        /* Phone input container fix */
        .phone-input,
        div[class*="phone"],
        .tel-input,
        div[class*="input_phone"] {
          display: flex !important;
          gap: 0px !important;
          width: 100% !important;
          padding:1px;
          max-width: 100% !important;
        }

        /* Country code dropdown - narrow width */
        .phone-input select,
        div[class*="phone"] select,
        div[class*="input_phone"] select {
          flex: 0 0 90px !important;
          width: 90px !important;
          max-width: 90px !important;
          min-width: 90px !important;
          border-right: none !important;
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
          padding-left: 8px !important;
          padding-right: 4px !important;
        }

        /* Phone number input - takes remaining space */
        .phone-input input,
        div[class*="phone"] input,
        div[class*="input_phone"] input {
          flex: 1 1 auto !important;
          width: auto !important;
          max-width: none !important;
          min-width: 0 !important;
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
          border-left: none !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        /* Ensure phone dropdown and input have same height */
        div[class*="input_phone"] select,
        div[class*="input_phone"] input {
          height: 46px !important;
          padding-top: 12px !important;
          padding-bottom: 12px !important;
        }

        /* Submit button - full width */
        button,
        input[type="submit"],
        button[type="submit"] {
          background-color: #7c3aed;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          padding: 12px 32px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          width: 100% !important;
          transition: background-color 0.2s ease;
          margin-top: 10px;
        }

        button:hover,
        input[type="submit"]:hover,
        button[type="submit"]:hover {
          background-color: #6d28d9;
        }

        /* Disabled button state */
        button:disabled,
        input[type="submit"]:disabled,
        button[type="submit"]:disabled {
          background-color: #5a21b6;
          opacity: 0.7;
          cursor: not-allowed;
          width: 100% !important;
        }

        /* Form layout */
        form {
          display: flex;
          flex-direction: column;
        }

        .form {
          gap: 8px !important;
        }

        .heading {
          margin-bottom: 30px !important;
          font-size: 34px !important;
          font-weight: bold !important;
        }

        .form-field,
        div[class*="field"] {
          margin-bottom: 20px;
        }

        /* Text elements */
        div, span {
          color: ${isWhiteTheme ? '#4b5563' : '#e5e5e5'};
        }

        /* Make paragraphs and their containers full width */
        p {
          width: 100% !important;
          max-width: 100% !important;
        }

        /* Ensure all divs take full width - except phone input containers */
        div:not([class*="phone"]):not([class*="phone-input"]):not([class*="logo"]):not([class*="image"]) {
          width: 100% ;
          max-width: 100% !important;
        }

        .country-select{
          width: 10% !important;
          max-width: 10% !important;
        }

        /* Images and logos */
        img {
          display: block !important;
          max-width: 100% !important;
          height: auto !important;
          width: auto !important;
        }

        /* Logo container - don't force full width */
        div[class*="logo"],
        div[class*="image"],
        .logo-container,
        .image-container {
          width: auto !important;
          max-width: 100% !important;
        }

        /* Links */
        a {
          color: #7c3aed;
          text-decoration: none;
        }

        a:hover {
          text-decoration: underline;
        }

        /* Required field indicator */
        .required {
          color: #ef4444;
        }

        .paragraph{
          text-wrap:auto !important;  
        }
      `;

      // Inject the style into shadow DOM
      shadowRoot.appendChild(style);
      return true;
    };

    // Try to inject styles immediately
    if (!injectStylesToShadowDOM()) {
      // If not ready, use MutationObserver to wait for the shadow DOM
      const observer = new MutationObserver(() => {
        if (injectStylesToShadowDOM()) {
          observer.disconnect();
        }
      });

      // Observe the document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Fallback timeouts: 100ms, 500ms, 1000ms
      const timeouts = [100, 500, 1000];
      const timeoutIds = timeouts.map(delay =>
        setTimeout(() => {
          if (injectStylesToShadowDOM()) {
            observer.disconnect();
          }
        }, delay)
      );

      return () => {
        observer.disconnect();
        timeoutIds.forEach(id => clearTimeout(id));
      };
    }
  }, [formId, backgroundColor]);

  return (
    <div className="w-full">
      <close-form id={formId} />
    </div>
  );
};

export default CloseFormWrapper;
